<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useOrderCalendarStore } from '../pages/orderCalendarStore';
import { storeToRefs } from 'pinia';
import { CalendarOrder } from '../domains/entities/CalendarOrder';
import { CalendarOrderService } from '../domains/services/CalendarOrderService';
import { IDate } from '@/coreDomainsModule/date';
import { DateService } from '@/coreDomainsModule/services/dateService';
import SvgLoader from '@/utils/SvgLoader.vue';
import { i18n } from '@/i18n';
import { OrderRepository } from '@/coreInfrastructureModule/Repositories/OrderRepository';

const t = i18n.t;

const orderRepository = new OrderRepository();
const store = useOrderCalendarStore();
const { state } = storeToRefs(store);
const { setDraggingOrder, moveDraggingOrder, isValidBulkOrderRules, isValidDailyDeliveryLimit, setModalInfo } = store;

/**
 * カレンダーの日付と発注を表示するコンポーネントのプロパティ
 * @property day - 表示する日付
 * @property events - その日のイベントのリスト
 */
const props = defineProps<{
  date: IDate;
  orders: CalendarOrder[];
  disabled?: boolean;
  isWeekend?: boolean;
}>();

/**
 * 「他N件」をクリックしたときに呼び出される関数
 * さらに多くのイベントの詳細を表示するためのハンドラ
 */
const showMore = () => {
  if (state.value.dateOpeningOtherOrderModal === undefined) {
    state.value.dateOpeningOtherOrderModal = props.date;
  } else {
    if (!DateService.equalYearMonthDayInfo(state.value.dateOpeningOtherOrderModal, props.date)) {
      state.value.dateOpeningOtherOrderModal = props.date;
    } else {
      state.value.dateOpeningOtherOrderModal = undefined;
    }
  }
};

/**
 * モーダルの位置を微調整する関数
 */
const tweakModalPosition = () => {
  // モーダルとトリガー要素の取得
  const modal = document.querySelector('.calendar-day__modal');
  const trigger = document.querySelector('.calendar-day__event--more');

  if (!modal || !trigger) {
    console.error('モーダルまたはトリガー要素が見つかりません。');
    return;
  }

  // ElementからHTMLElementにキャスト
  if (!(trigger instanceof HTMLElement) || !(modal instanceof HTMLElement)) {
    console.error('トリガー要素またはモーダルがHTMLElementではありません。');
    return;
  }

  // トリガー要素の位置とサイズを取得
  const triggerRect = trigger.getBoundingClientRect();

  // モーダルが非表示状態でサイズを取得できない場合、一時的に表示（visibilityを使って見えなくする）
  modal.style.visibility = 'hidden';
  modal.style.display = 'block';
  const modalWidth = modal.offsetWidth;
  const modalHeight = modal.offsetHeight;
  modal.style.visibility = ''; // visibility設定をクリア
  modal.style.display = 'none'; // display設定をもとに戻す

  // モーダルを表示する位置を計算
  const leftPosition = triggerRect.right; // トリガーの右端
  const topPosition = triggerRect.top + window.scrollY + triggerRect.height / 2 - modalHeight / 2; // 垂直中央揃え

  // モーダルにスタイルを適用
  modal.style.position = 'absolute';
  modal.style.left = `${leftPosition}px`;
  modal.style.top = `${topPosition}px`;

  // モーダルを表示
  modal.style.display = 'block';
};

const isShowModal = computed(() => {
  if (state.value.dateOpeningOtherOrderModal === undefined) {
    return false;
  }
  return DateService.equalYearMonthDayInfo(state.value.dateOpeningOtherOrderModal, props.date);
});

/**
 * ドラッグ操作が開始されたときに呼び出されるハンドラ
 * ドラッグされる要素の情報をデータ転送オブジェクトに設定する
 * @param event - イベントオブジェクト
 * @param order - ドラッグされる発注情報
 */
const handleDragStart = (event: DragEvent, order: CalendarOrder) => {
  setDraggingOrder(order);
  // DOMを不可視にする
  // 瞬時に不可視にすると、ドラッグ操作が開始された瞬間に要素が消えるため、少し遅らせる
  setTimeout(() => {
    if (event.target instanceof HTMLElement) {
      event.target.style.opacity = '0';
    }
  }, 1);
};

/**
 * ドラッグ操作が終了したときに呼び出されるハンドラ
 * ドラッグされる要素の表示を元に戻す
 * @param event - イベントオブジェクト
 */
const handleDragEnd = (event: Event) => {
  if (event.target instanceof HTMLElement) {
    // DOMを可視に戻す
    event.target.style.opacity = '1';
  }
};

/**
 * ドロップされた時に呼び出されるハンドラ
 */
const handleDrop = async () => {
  await moveDraggingOrder(props.date, orderRepository);
};

/**
 * 表示される発注情報の文言を取得する
 * @param order - 発注情報
 */
const getDisplayOrder = (order: CalendarOrder) => {
  return `${order.businessName} ${order.amount.addComma()}${order.unit ?? ''}`;
};

// 表示するイベントの計算されたプロパティ。最大3つまで。
const orderToShow = computed(() => props.orders.slice(0, 3));

// 「他N件」の計算されたプロパティ。3つを超えるイベントの数。
const remainingOrders = computed(() => Math.max(props.orders.length - 3, 0));

/**
 * アラートを左方向に展開するかを返す。木曜日と金曜日と土曜日はTrueを返す。
 */
const isAlertLeft = computed(() => {
  const dateObject = DateService.convertIDateToDate(props.date);
  if (dateObject.getDay() === 4 || dateObject.getDay() === 5 || dateObject.getDay() === 6) {
    return true;
  }
  return false;
});

const isShowDeliveryLimitAlert = computed(() => {
  return !props.disabled && !isValidDailyDeliveryLimit(props.date) && !isCheckDeliveryLimit.value;
});

const isShowBulkOrderRulesAlert = computed(() => {
  return !props.disabled && !isValidBulkOrderRules(props.date) && !isCheckBulkOrderRules.value;
});

const isDragging = ref(false);
const modalPosition = ref({ x: 0, y: 0 });

const isShowAlertModal = ref(false);
const isCheckDeliveryLimit = ref(false);
const isCheckBulkOrderRules = ref(false);
const isCheckBoxTrueDeliveryLimit = ref(false);
const isCheckBoxTrueBulkOrderRules = ref(false);

/**
 *　モーダルの現在位置を更新するための関数
 * @param x - モーダルの新しい位置
 * @param y　- モーダルの新しい位置
 */
const updateModalPosition = (x: number, y: number) => {
  modalPosition.value.x = x;
  modalPosition.value.y = y;
};

/**
 * モーダルをドラッグするための関数
 * @param event - マウスイベント
 */
const startDrag = (event: MouseEvent) => {
  isDragging.value = true;
  const modal = event.target as HTMLElement;
  const offsetX = event.clientX - modal.getBoundingClientRect().left;
  const offsetY = event.clientY - modal.getBoundingClientRect().top;

  /**
   * マウスが移動したときに呼び出される関数
   * @param moveEvent - マウスイベント
   */
  const handleMouseMove = (moveEvent: MouseEvent) => {
    if (isDragging.value) {
      const x = moveEvent.clientX - offsetX;
      const y = moveEvent.clientY - offsetY;
      updateModalPosition(x, y);
    }
  };

  /**
   * マウスが離されたときに呼び出される関数
   */
  const handleMouseUp = () => {
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
    isDragging.value = false;
  };

  window.addEventListener('mousemove', handleMouseMove);
  window.addEventListener('mouseup', handleMouseUp);
};

/**
 * 日付がクリックされたときに呼び出されるハンドラ
 */
const clickDay = () => {
  if (props.orders.length === 0) return;
  // 外部要因でモーダルが閉じた場合に再度開くための対応 （isOpenがtrueの場合、true -> trueのため変更が検知されない）
  if (state.value.modalInfo.isOpen === true) {
    setModalInfo(false, undefined);
  }
  setModalInfo(true, props.date);
};

onMounted(() => {
  const modalElement = document.querySelector('.calendar-day__modal');
  modalElement?.addEventListener('mousedown', startDrag as EventListener);
});

onUnmounted(() => {
  const modalElement = document.querySelector('.calendar-day__modal');
  modalElement?.removeEventListener('mousedown', startDrag as EventListener);
});
</script>

<template>
  <div :class="`calendar-day ${disabled ? 'calendar-day--disabled' : ''}`" @dragover.prevent @drop="handleDrop">
    <div
      :class="`calendar-day__date ${isWeekend ? 'calendar-day__date--weekend' : ''} ${
        orders.length === 0 ? 'calendar-day__date--no-pointer' : ''
      }`"
      @click="clickDay"
    >
      {{ date.day }}
    </div>
    <SvgLoader
      v-show="isShowDeliveryLimitAlert || isShowBulkOrderRulesAlert"
      class="calendar-day__alert-icon"
      name="calendarAlert"
      @click="isShowAlertModal = !isShowAlertModal"
    />
    <div
      v-show="isShowAlertModal && (isShowDeliveryLimitAlert || isShowBulkOrderRulesAlert)"
      :class="`calendar-day__alert-modal ${isAlertLeft ? 'calendar-day__alert-modal--left' : ''}`"
    >
      <div v-show="isShowBulkOrderRulesAlert" class="calendar-day__alert-container">
        <h1 class="calendar-day__alert-title">{{ t('まとめ発注アラート') }}</h1>
        <p class="calendar-day__alert-text">
          {{ t('仕入先との発注ルールに則っていません。') }}<br />
          {{ t('納期と数量の調整をお願いします。') }}
        </p>
        <div class="calendar-day__modal-content">
          <div
            v-for="(order, index) in props.orders"
            :key="index"
            :class="`calendar-day__event ${CalendarOrderService.isChangeableCrossDate(order) ? 'calendar-day__event--draggable' : ''}`"
          >
            {{ getDisplayOrder(order) }}
          </div>
        </div>
        <p class="calendar-day__alert-text">
          <input v-model="isCheckBoxTrueBulkOrderRules" class="calendar-day__alert-checkbox" type="checkbox" />{{
            t('仕入先から了承受領済み')
          }}
        </p>
        <button :class="`calendar-day__alert-button`" :disabled="!isCheckBoxTrueBulkOrderRules" @click="isCheckBulkOrderRules = true">
          {{ t('アラートを取り消す') }}
        </button>
      </div>
      <div v-show="isShowDeliveryLimitAlert && isShowBulkOrderRulesAlert" class="calendar-day__alert-partition-line" />
      <div v-show="isShowDeliveryLimitAlert" class="calendar-day__alert-container">
        <h1 class="calendar-day__alert-title">{{ t('受入上限オーバー') }}</h1>
        <p class="calendar-day__alert-text">
          {{ t('1日の受入上限を超過しています。') }}<br />
          {{ t('納期の調整をお願いします。') }}
        </p>
        <div class="calendar-day__modal-content">
          <div
            v-for="(order, index) in props.orders"
            :key="index"
            :class="`calendar-day__event ${CalendarOrderService.isChangeableCrossDate(order) ? 'calendar-day__event--draggable' : ''}`"
          >
            {{ getDisplayOrder(order) }}
          </div>
        </div>
        <p class="calendar-day__alert-text">
          <input v-model="isCheckBoxTrueDeliveryLimit" class="calendar-day__alert-checkbox" type="checkbox" />{{
            t('倉庫から超過納入了承を受領済み')
          }}
        </p>
        <button class="calendar-day__alert-button" :disabled="!isCheckBoxTrueDeliveryLimit" @click="isCheckDeliveryLimit = true">
          {{ t('アラートを取り消す') }}
        </button>
      </div>
    </div>
    <div class="calendar-day__events">
      <div
        v-for="(order, index) in orderToShow"
        :key="index"
        :class="`calendar-day__event ${CalendarOrderService.isChangeableCrossDate(order) ? 'calendar-day__event--draggable' : ''} ${
          index !== 2 ? 'calendar-day__event--not-last' : ''
        }`"
        :draggable="CalendarOrderService.isChangeableCrossDate(order)"
        @dragstart="(e) => handleDragStart(e, order)"
        @dragend="handleDragEnd"
      >
        {{ getDisplayOrder(order) }}
      </div>
      <div v-if="remainingOrders > 0" class="calendar-day__event--more" @click="showMore">
        {{ t('他') }}{{ remainingOrders }}{{ t('件') }}
        <div v-show="isShowModal" class="calendar-day__modal">
          <h2 class="calendar-day__modal-title">{{ t('全ての発注', { month: date.month, day: date.day }) }}</h2>
          <div class="calendar-day__modal-content">
            <div
              v-for="(order, index) in props.orders"
              :key="index"
              :class="`calendar-day__event ${CalendarOrderService.isChangeableCrossDate(order) ? 'calendar-day__event--draggable' : ''}`"
              :draggable="CalendarOrderService.isChangeableCrossDate(order)"
              @dragstart="(e) => handleDragStart(e, order)"
              @dragend="handleDragEnd"
            >
              {{ getDisplayOrder(order) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.calendar-day {
  position: relative;
  background-color: $semanticWhite;
  border: 1px solid $borderDefault;

  &:not(.calendar-day--disabled):hover {
    background-color: $semanticPrimarySubdued;
  }

  &--disabled {
    opacity: 50%;
    pointer-events: none;
  }

  &__date {
    font-family: Noto Sans JP;
    font-size: 16px;
    font-weight: 700;
    line-height: 23px;
    letter-spacing: 0em;
    text-align: center;

    text-align: center;
    color: $textSecondary;
    padding: 8px 0;
    cursor: pointer;

    &--weekend {
      color: $optionalPink;
    }

    &--no-pointer {
      cursor: default;
    }
  }

  &__alert-icon {
    position: absolute;
    top: 10px;
    right: 10px;
    padding: 2px;
  }

  &__events {
    display: grid;
    grid-template-rows: repeat(3, minmax(20px, 1fr));
    grid-template-columns: auto auto;
    grid-gap: 4px;
    justify-content: start;
    justify-items: start;
    align-items: center;
    padding: 16px;
    padding-top: 10px;
  }

  &__event {
    font-family: Noto Sans JP;
    font-size: 12px;
    font-weight: 700;
    line-height: 12px;
    letter-spacing: 0.4;
    text-align: left;
    color: $semanticSuccess;
    padding: 4px 8px;

    &--not-last {
      grid-column: 1 / span 2;
    }

    &--draggable {
      color: $textSecondary;
      background-color: $semanticWhite;
      cursor: pointer;

      padding: 3px 7px;
      border: 1px solid $borderSecondary;
      border-radius: 4px;
    }
  }

  &__event--more {
    font-family: Noto Sans JP;
    font-size: 12px;
    font-weight: 700;
    line-height: 12px;
    letter-spacing: 0.4000000059604645px;
    text-align: left;

    color: $textSecondary;
    position: relative;

    cursor: pointer;
    user-select: none;
  }

  &__modal {
    padding: 16px 24px;
    background-color: $semanticWhite;
    box-shadow: 0px 0px 22px 0px #80808066;
    display: grid;
    grid-template-rows: auto auto;
    gap: 16px;
    position: absolute;
    z-index: 1;
    left: 100%; // ボタンの右側に配置
    transform: translateY(-50%); // 垂直方向の中央揃えのための調整
    top: 50%;
    margin-left: 12px;
    border-radius: 8px;

    width: auto; // 必要に応じて幅を調整
    min-width: 200px; // 最小幅を設定
    z-index: 10; // 他の要素より前面に表示

    &-title {
      font-family: Noto Sans JP;
      font-size: 16px;
      font-weight: 700;
      line-height: 16px;
      letter-spacing: 0em;
      text-align: center;
      color: $textDefault;
    }

    &-content {
      background-color: $backgroundDefault;
      border-radius: 8px;
      padding: 8px;
      display: grid;
      gap: 8px;
      justify-content: start;
      align-content: start;
      overflow-y: auto;
      max-height: 176px;
    }
  }

  &__alert {
    &-modal {
      width: max-content;
      z-index: 10000;
      position: absolute;
      top: 20px;
      right: 0;
      display: grid;
      grid-template-columns: auto 2px auto;
      background-color: $semanticWhite;
      box-shadow: 0px 0px 22px 0px #80808066;
      border-radius: 8px;
      transform: translateX(100%) translateY(-50%);

      &::before {
        content: '';
        position: absolute;
        top: 50%;
        left: -12px; // 三角形を要素の左側に配置
        transform: translateY(-50%) rotate(45deg); // 三角形を45度回転
        width: 30px; // 三角形の幅
        height: 30px; // 三角形の高さ
        background-color: $semanticWhite; // 三角形の背景色を吹き出しと同じに
      }

      &--left {
        transform: translateX(0%) translateY(-50%);
        right: 50px;

        &::before {
          left: auto; // 三角形を要素の右側に配置
          right: -10px; // 三角形を要素の右側に配置
          transform: translateY(-50%) rotate(-135deg); // 三角形を-135度回転
        }
      }
    }

    &-container {
      padding: 16px 24px;
      display: grid;
      gap: 16px;
      width: max-content;
      background-color: $semanticWhite;
      border-radius: 8px;
    }

    &-title {
      font-family: Noto Sans JP;
      font-size: 16px;
      font-weight: 700;
      line-height: 16px;
      letter-spacing: 0em;
      text-align: center;
      color: $semanticError;
    }

    &-text {
      font-family: Noto Sans JP;
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      letter-spacing: 0em;
      text-align: center;
      color: $textDefault;
    }

    &-button {
      height: 40px;
      background: $semanticError;
      padding: 12px 16px;
      color: $semanticWhite;
      font-family: Noto Sans JP;
      font-size: 16px;
      font-weight: 700;
      line-height: 16px;
      letter-spacing: 0em;
      text-align: center;

      &:disabled {
        background-color: $surfaceSecondary;
        border: 1px solid $surfaceSecondary;
        color: $textDisable;
      }
    }

    &-partition-line {
      background-color: $borderDefault;
      margin: 16px 0;
    }
  }
}
</style>
